import { z } from 'zod';
import { Transaction } from '../common';
import { IdentityProviders } from '../identity';
import { Streamer } from '../streamer/streamer';

export const INTEGRATION_TYPES = [
  // 'spotify',
  'streamlabs',
  'streamelements',
  'twitch',
  'youtube',
] as const;
export const integrationTypeSchema = z.enum(INTEGRATION_TYPES);
export type IntegrationType = z.infer<typeof integrationTypeSchema>;

export type BaseIntegration = {
  id: number;
  type: IntegrationType;
  created: Date;
  status: 'connected' | 'disconnected';

  settings: Record<string, unknown>;
  privateSettings: Record<string, string | number | boolean>;

  streamer?: Streamer;
  streamerId?: number;
};

export type StreamlabsSettings = {
  sendChatMessage: boolean;
};

export type StreamlabsPrivateSettings = {
  socketToken: string | null;
};

export type StreamlabsIntegration = BaseIntegration & {
  type: 'streamlabs';
  settings: StreamlabsSettings;
  privateSettings: StreamlabsPrivateSettings;
};

export type StreamelementsSettings = {
  sendChatMessage: boolean;
};

export type StreamelementsIntegration = BaseIntegration & {
  type: 'streamelements';
  settings: StreamelementsSettings;
};

export type TwitchBotIntegration = BaseIntegration & {
  type: 'twitch:bot';
};

type GetIntegrationResult<K> = K extends 'streamlabs'
  ? StreamlabsIntegration
  : K extends 'streamelements'
    ? StreamelementsIntegration
    : K extends 'twitch'
      ? TwitchStreamerIntegration
      : K extends 'youtube'
        ? YoutubeStreamerIntegration
        : never;

export function getIntegration<T extends Integration, K extends T['type']>(
  integration: T,
  type: K,
): GetIntegrationResult<K> {
  switch (type) {
    case 'streamlabs':
      return integration as unknown as GetIntegrationResult<K>;

    case 'streamelements':
      return integration as unknown as GetIntegrationResult<K>;

    case 'twitch':
      return integration as unknown as GetIntegrationResult<K>;

    case 'youtube':
      return integration as unknown as GetIntegrationResult<K>;

    default:
      throw new Error(`Invalid integration type: ${type}`);
  }
}

export type TwitchStreamerSettings = {
  botConnected: boolean;
  user: RequestTypeSettings;
  follower: RequestTypeSettings;
  subscriber: RequestTypeSettings;
  subscriberT2: RequestTypeSettings;
  subscriberT3: RequestTypeSettings;
  vip: RequestTypeSettings;
};

export type TwitchStreamerIntegration = BaseIntegration & {
  type: 'twitch';
  settings: TwitchStreamerSettings;
};

export type RequestTypeSettings = {
  concurrent: number;
  enabled: boolean;
  limit: boolean;
  requests: number;
};

export type YoutubeStreamerSettings = {
  user: RequestTypeSettings;
  subscriber: RequestTypeSettings;
  membershipsLevels: {
    [name: string]: RequestTypeSettings;
  };
};

export type YoutubeStreamerIntegration = BaseIntegration & {
  type: 'youtube';
  settings: YoutubeStreamerSettings;
};

export type Integration =
  | StreamlabsIntegration
  | StreamelementsIntegration
  | TwitchBotIntegration
  | TwitchStreamerIntegration
  | YoutubeStreamerIntegration;

export interface IntegrationForSocket {
  socketToken: string;
  streamerId: number;
}

export type IntegrationPublic = Pick<Integration, 'id' | 'type' | 'settings'> & {
  status: Integration['status'] | 'missing-scopes';
};

export type IntegrationUpdateParams = Pick<Partial<Integration>, 'settings'>;

export type IntegrationMap = {
  ['twitch:streamer']?: TwitchStreamerIntegration;
  ['streamlabs']?: StreamlabsIntegration;
  ['streamlabs:integration']?: StreamlabsIntegration;
  ['youtube:streamer']?: YoutubeStreamerIntegration;
  [key: string]: Integration | undefined;
};

type GetStreamerSettingsResult<T> = T extends 'twitch'
  ? TwitchStreamerSettings | undefined
  : T extends 'youtube'
    ? YoutubeStreamerSettings | undefined
    : never;

export function getStreamerSettings<T extends IdentityProviders>(
  streamer: Pick<Streamer, 'integrationsMap'>,
  platform: T,
): GetStreamerSettingsResult<T> {
  const settings = streamer.integrationsMap?.[`${platform}:streamer`]?.settings;
  if (!settings) {
    return undefined as unknown as GetStreamerSettingsResult<T>;
  }
  // TODO: figure this out
  return settings as unknown as GetStreamerSettingsResult<T>;
}

export function isIdentityProvider(provider: string): provider is IdentityProviders {
  return provider === 'twitch' || provider === 'google';
}

export interface DonationEvent {
  streamerId?: number;
  identityId: string;
  _id: string;
  name: string;
  message: string;
  amount: number;
  type: Transaction['sourceType'];
}

export interface DonationEventInput extends DonationEvent {
  sendMessage: boolean;
}
